import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FormBuilder, FormGroup, Validators, FormControl, ValidatorFn } from '@angular/forms';

import { environment } from '../../environments/environment';
import { NotificationService } from './notification-service';
import { SessionStorageService } from './session-storage.service';

import { Form, FieldTemplate, FormTemplate, FormValues, FieldValue, FormResponse } from '../models/form';
import { DynamicForm, DynamicFormField } from '../models/dynamic-form';
import { CentralNote } from '../models/central-notes';



@Injectable( {
  providedIn: 'root'
} )
export class FormService
{
  private API_BASE = environment.apiBaseUrl;
  private API_USER = environment.apiEndpoints.user;

  constructor(
    private _http: HttpClient,
    private _fb: FormBuilder,
    private _notificationService: NotificationService,
    private _storageService: SessionStorageService,
  ) { }

  public SpecialControls = [
    "ADMIT_VISIT",
    "APPOINTMENTS",
    "APPS_COMP_SUB",
    "APPT_DATE_PROVIDER",
    "APPT_DATE_SITE",
    "ASSESSMENT_AREA",
    "P2_APPOINTMENTS",
    "APPT_DATE",
    "BIRTH_INFORMATION",
    "BLOOD_PRESSURE",
    "BMI",
    "CARDIAC_APPOINTMENTS",
    "CARE_GAP_GRID",
    "CARE_TEAM_PICKER",
    "CARE_TEAM_SUPPORT_PICKER",
    "CENTRAL_NOTES",
    "CHECK_IN_DATE",
    "CHRONIC_CONDITIONS",
    "CLIENT_ADDRESS",
    "CLIENT_ALT_ID_FIELD",
    "CLIENT_CHRONIC_CONDITIONS_V2",
    "CLIENT_MED_LIST",
    "CLIENT_REFERRAL_SOURCES",
    "COMMUNICATIONS_CONTROL",
    "CONTACTS_GRID",
    "CTI_ENCOUNTER_ENTRY",
    "EDITABLE_DROP_DOWN",
    "EDUCATION_DOC",
    "FALL_SCORE",
    "FORM_LINK",
    "HAP_DIAGNOSIS",
    "HAP_PREVIEW_LINK",
    "HAP_SHORT_TERM_GOALS",
    "HEALTH_HOMES_TIME_ACTIVITY",
    "HEALTHY_CHANGES_REVIEW",
    "HOUSEHOLD_MEMBERS_SELECT",
    "INSURANCE_CONTROL",
    "MED_ASSESS_CHART_LINK",
    "MEDS_CATS",
    "MED_LIST",
    "MED_REVIEWER_PICKER",
    "PATHWAYS_GRID",
    "PCHB_REFERRAL",
    "POUNDS_GRAMS",
    "PROVIDER_PICKER",
    "RECALCULATE_RISKQ_BUTTON",
    "RECOVERY_HOUSE_CHECKIN",
    "RECOVERY_HOUSE_INTERVIEW",
    "RECOVERY_HOUSE_WAITLIST",
    "RECOVERY_HOUSING_PREF",
    "REFERRAL_CONTROL_V2",
    "RELATIONSHIP_CONTROL",
    "REORDER_DROP_DOWN",
    "SIGNATURE",
    "UDS_REFERRAL_NOTES",
    "VERBAL_CONSENT",
    "WAITLIST_CLIENT_CHECKIN"
  ];

  public async getFormDefinition( pct: string, formId: number, hub: string | null, agency: number | null ): Promise<FormTemplate | undefined>
  {
    const params = `/${pct}/${formId}/${hub}/${agency}`;
    const form = await this._http.get<FormTemplate>( `${this.API_BASE}${this.API_USER.forms.template}${params}` ).toPromise();
    return form;
  }

  public async getFormValues( pct: string, formId: number, clientId: string | null, visitId: string | null ): Promise<FormValues | undefined>
  {
    const params = `/${pct}/${formId}/${clientId}/${visitId}`;
    const values = await this._http.get<FormValues>( `${this.API_BASE}${this.API_USER.forms.values}${params}` ).toPromise();
    return values;
  }

  public async getCentralNotes( pct: string, formId: number, clientId: string | null, visitId: string | null, noteType: string | null ): Promise<CentralNote[] | undefined>
  {
    const params = `/${clientId}/${pct}/${formId}/${visitId}/${noteType}`;
    const values = await this._http.get<CentralNote[]>( `${this.API_BASE}${this.API_USER.forms.centralNotes}${params}` ).toPromise();
    return values;
  }

  public generateDynamicFormTemplate( fields: FieldTemplate[] ): FormGroup
  {
    const group: { [key: string]: FormControl } = {};
    fields.forEach( field => { group[field.fieldId] = new FormControl( field ); }
    );
    return this._fb.group( group );
  }

  public generateDynamicForm( fields: FieldTemplate[] ): FormGroup
  {

    const group: { [key: string]: FormControl } = {};

    fields
      .filter( f => this.isValidField( f ) )
      .forEach( field =>
      {
        const validators = [];

        if ( field.isRequired )
        {
          validators.push( Validators.required );
        }
        if ( field.validationRegex )
        {
          validators.push( Validators.pattern( field.validationRegex ) );
        }
        if ( field.fieldType.toUpperCase() === 'EMAIL' )
        {
          validators.push( Validators.email );
        }
        if ( field.fieldType.toUpperCase() === 'NUMBER' )
        {
          validators.push( Validators.pattern( '^[0-9]*$' ) );
        }
        if ( field.fieldType.toUpperCase() === 'PHONE' )
        {
          validators.push( Validators.pattern( '^[0-9\\-\\+\\(\\) ]*$' ) );
        }

        const value = field.defaultValue || '';
        group[field.fieldId] = new FormControl( value, validators );


      } );

    return this._fb.group( group );
  }

  public generateUpdatePayload( pct: string, formId: number, clientId: string | null, visitId: string | null, userHub: string | null, userAgency: number | null, fields: FieldTemplate[], form: FormGroup ): FormValues
  {
    const values: FormValues = {
      formPct: pct,
      formId: formId,
      clientId: clientId || '',
      visitId: visitId || '',
      userFormHubId: userHub,
      userFormAgencyId: userAgency,
      fieldValues: []
    };
    fields.forEach( field =>
    {
      if ( this.isValidField( field ) && form.get( `${field.fieldId}` ) )
      {
        const isDate = field.fieldType.toUpperCase().startsWith( 'DATE' ) || false;
        const controlValue = form.get( `${field.fieldId}` )?.value || null;
        const value = isDate ? this.getDateStringValue( controlValue ) : controlValue;
        values.fieldValues.push( {
          fieldId: field.fieldId,
          dbColumnName: field.dbColumnName,
          dbTableName: field.dbTableName,
          fieldValueString: value !== null ? value.toString() : ''
        } );
      }
    } );
    return values;
  }

  public populateFormWithValues( form: FormGroup, fields: FieldValue[], fieldsDefinition: FieldTemplate[], readOnly: boolean = false ): void
  {
    for ( const field of fields )
    {
      if ( form.contains( `${field.fieldId}` ) )
      {
        const stringValue = field.fieldValueString || '';
        let value: string | string[] | Date = stringValue;

        const fieldDefinition = fieldsDefinition.filter( f => f.fieldId === field.fieldId )[0] || null;

        switch ( this.getFieldRenderType( fieldDefinition ) )
        {
          case 'date':
            value = this.isStringDate( stringValue ) ? new Date( stringValue ) : stringValue;
            break;
          case 'multiSelect':
            value = stringValue.length > 0 ? stringValue.split( ',' ) : [];
            break;
          default:
            break;
        }

        form.get( `${field.fieldId}` )?.patchValue( value );

        if ( readOnly )
        {
          form.get( `${field.fieldId}` )?.disable()
        }

      }
    }

  }

  public isValidField( field: FieldTemplate ): boolean
  {
    const valid = ( field.dbColumnName !== null && field.dbColumnName.length > 0 )
      || this.isSpecialControl( field );
    return valid;
  }

  public isSpecialControl( field: FieldTemplate ): boolean
  {
    return this.SpecialControls.includes( field.fieldType.toUpperCase() );
  }

  public saveForm( values: FormValues ): Promise<FormResponse | undefined>
  {
    const result = this._http.put<FormResponse>( `${this.API_BASE}${this.API_USER.forms.update}`, values ).toPromise();;
    result.then(
      () =>
      {
        const clientId = values.clientId || '';
        if ( this._storageService.has( `${clientId}_activeForms` ) )
        {
          this._storageService.delete( `${clientId}_activeForms` );
        }

        this._notificationService.showSuccessMessage( 'Form saved successfully.' );
      }          ,
    );
    return result;
  }

  public getDateStringValue( value: Date ): string
  {
    const date: Date = value;
    let formatted = '';
    if ( date )
    {
      const mm = String( date.getMonth() + 1 ).padStart( 2, '0' );
      const dd = String( date.getDate() ).padStart( 2, '0' );
      const yyyy = date.getFullYear();
      formatted = `${mm}/${dd}/${yyyy}`;
    }
    return formatted;
  }

  public isStringDate( value: string ): boolean
  {
    if ( !value || typeof value !== 'string' )
    {
      return false;
    }
    // Try to parse the string as a date
    const date = new Date( value );
    // Check if the date is valid
    return !isNaN( date.getTime() );
  }

  public getFieldRenderType( field: FieldTemplate ): string
  {
    const type = field.fieldType?.toUpperCase() || '';
    if ( type === 'LARGE_TEXT' ) return 'largeText';
    if ( type === 'EMPTY_LINE' && field.labelText ) return 'emptyLineWithLabel';
    if ( type === 'EMPTY_SLOT' && field.labelText ) return 'emptySlotWithLabel';
    if ( type === 'BOLD_TEXT' && field.labelText ) return 'boldTextWithLabel';
    if ( type === 'EMPTY_LINE' && !field.labelText ) return 'emptyLine';
    if ( type.startsWith( 'DATE' ) ) return 'date';
    if ( type.startsWith( 'TIME' ) ) return 'time';
    if ( type.startsWith( 'MSDD' ) ) return 'multiSelect';
    if ( type.startsWith( 'DD' ) ) return 'dropdown';
    if ( type.startsWith( 'RADIO' ) ) return 'radio';
    if ( type.startsWith( 'PHONE' ) ) return 'phone';
    if ( type.startsWith( 'EMAIL' ) ) return 'email';
    if ( type.startsWith( 'NUMBER' ) ) return 'number';
    if ( type.startsWith( 'TEXT' ) ) return 'text';
    if ( type.startsWith( 'CARE_TEAM_PICKER' ) ) return 'careTeamPicker';
    if ( type.startsWith( 'CLIENT_MED_LIST' ) ) return 'clientMedList';
    if ( type.startsWith( 'CENTRAL_NOTES' ) ) return 'centralNotes';
    if ( type.startsWith( 'ADMIT_VISIT' ) ) return 'adminVisit';
    return 'unknown';
  }

  public async GetFormPreview( pct: string, formId: number, hubId: string | null, agencyId: number | null ): Promise<DynamicForm | undefined>
  {
    const hubparam = hubId ? `?hubId=${hubId}` : '';
    const agencyparam = agencyId ? `&agencyId=${agencyId}` : '';
    const params = `${pct}/${formId}${hubparam}${agencyparam}`;

    const form = await this._http.get<any>( `${this.API_BASE}${this.API_USER.forms.preview}/${params}` ).toPromise();
    return form.data as DynamicForm;
  }

  public async GetFormEdit( pct: string, formId: number, clientId: string, visitId: string ): Promise<DynamicForm | undefined>
  {

    const params = `${pct}/${formId}/${clientId}/${visitId}`;

    const form = await this._http.get<any>( `${this.API_BASE}${this.API_USER.forms.edit}/${params}` ).toPromise();
    return form.data as DynamicForm;
  }

  public async GetFormCreate( pct: string, formId: number, clientId: string ): Promise<DynamicForm | undefined>
  {
    const params = `${pct}/${formId}/${clientId}`;

    // Special case for T6 and T175 forms
    console.log( `${pct}${formId}` );
    if ( `${pct}${formId}` === 'T6' || `${pct}${formId}` === 'T175' )
    {
      const form = await this._http.get<any>( `${this.API_BASE}${this.API_USER.forms.create}/client` ).toPromise();
      return form.data as DynamicForm;
    }

    const form = await this._http.get<any>( `${this.API_BASE}${this.API_USER.forms.create}/${params}` ).toPromise();
    return form.data as DynamicForm;
  }

  public async GetFormView( pct: string, formId: number, clientId: string, visitId: string ): Promise<DynamicForm | undefined>
  {

    const params = `${pct}/${formId}/${clientId}/${visitId}`;

    const form = await this._http.get<any>( `${this.API_BASE}${this.API_USER.forms.view}/${params}` ).toPromise();
    return form.data as DynamicForm;
  }

  public async GetFormGroup( fields: DynamicFormField[], mode: string = 'view' ): Promise<FormGroup>
  {
    const group: { [key: string]: FormControl } = {};
    const elements = fields.filter( f => !!f.renderType?.element );
    let validators: ValidatorFn[] = [];


    elements.forEach( field =>
    {
      // setup validators
      validators = [];
      field.renderType.validations?.forEach( validation =>
      {
        switch ( validation.type )
        {
          case 'required':
            validators.push( Validators.required );
            break;
          case 'email':
            validators.push( Validators.email )
            break
          case 'pattern':
            validators.push( Validators.pattern( validation.value ) );
            break;
        }
      } );

      // setup value or default value
      let value: string | string[] | Date = field.value || field.defaultValue || '';
      switch ( field.renderType.element )
      {
        case 'date-picker':

          if ( field.renderType.type === 'time' )
          {
            value = new Date( `${this.getDateStringValue( new Date() )} ${value.toString()}` );
          }
          else
          {
            value = this.isStringDate( value.toString() ) ? new Date( value.toString() ) : value;
          }

          break;
        case 'select':
          if ( field.renderType.type === 'multiple' )
          {
            value = value.toString().length > 0 ? value.toString().split( ',' ) : [];
          }
          break;
        default:
          break;
      }

      const control = new FormControl( value, validators );
      if ( mode === 'view' )
      {
        control.disable();
      }
      const controlKey = `${field.name || field.fieldType}-${field.fieldId.toString()}`;
      group[controlKey] = control;

    } );


    return this._fb.group( group );
  }

  public async GetFormGroupPayload( clientId: string | null, visitId: string | null, hubId: string | null, agencyId: number | null, form: DynamicForm, formGroup: FormGroup ): Promise<FormValues>
  {
    const values: FormValues = {
      formPct: form.pct,
      formId: form.formId,
      clientId: clientId || '',
      visitId: visitId || '',
      userFormHubId: hubId,
      userFormAgencyId: agencyId,
      fieldValues: []
    };
    form.fields.forEach( field =>
    {
      if ( !!field.name && formGroup.get( `${field.name}-${field.fieldId}` ) )
      {
        const isDate = field.fieldType.toUpperCase().startsWith( 'DATE' ) || false;
        const controlValue = formGroup.get( `${field.name}-${field.fieldId}` )?.value || null;
        const value = isDate ? this.getDateStringValue( controlValue ) : controlValue;
        values.fieldValues.push( {
          fieldId: field.fieldId,
          dbColumnName: field.name,
          dbTableName: '',
          fieldValueString: value !== null ? value.toString() : ''
        } );
      }
    } );

    return values;  
  }

  public async SaveForm( form: DynamicForm ): Promise<DynamicForm | undefined>
  {
    const result = await this._http.post<DynamicForm>( `${this.API_BASE}${this.API_USER.forms.save}`, form ).toPromise();
    if ( result )
    {
      this._storageService.delete( `${form.clientId}_activeForms` );
    }
    return Promise.resolve(result);
  }

}
